import { Guid } from "client_legacy/ndaw/guid";
import { environmentHttpClient } from "../httpClient";
import {
	SaveAppColorModel
} from "../Apps/common";
import {
	ClientValidationErrorModel,
	PermissionSetModel,
	KeyValuePair
} from "../common";
import {
	AppImageDisplayInfo,
	AppImageIdentifier
} from "../AppIcon/common";
import { UsersAndUserGroupsDataModel } from "../UserGroupsManagement/userGroupsManagement";
import { IntegrationFlowAuthenticationKind } from "./integrationBuilderAuthentication";
import { IntegrationSharedSettingOptionModel } from "./integrationBuilderApps";
import { TagSaveModel, TagViewModel } from "../Tags/tags";

export type KeyValuePairSaveModel = {
	key: string;
	value: string;
};

export type IntegrationFlowHistoryModel = {
	id: number;
	kind: IntegrationFlowKind;
	versions: readonly IntegrationFlowVersionModel[];
};

export type IntegrationFlowVersionModel = {
	id: Guid;
	name: string;
	status: IntegrationFlowStatus;
	stepsCount: number;
	capabilities: readonly IntegrationFlowCapabilityKind[];
	tags: readonly TagViewModel[];
};


export enum RequestMethod {
	Get = 1,
	Post = 2
}

export enum StepKind {
	Request = 1,
	Mapping = 2,
	PerItemRequest = 3
}

type BaseRequestStepConfigurationViewModel = {
	name: string;
	authenticationId: number | null;
	method: RequestMethod;
	url: string;
	headers: readonly KeyValuePair<string, string>[];
	queryParameters: readonly KeyValuePair<string, string>[];
	body: string;
};

export type PerItemRequestStepConfigurationViewModel = BaseRequestStepConfigurationViewModel & {
	kind: StepKind.PerItemRequest;
	stepCollection: string;
	maxItems: number;
};

export type RequestStepConfigurationViewModel = BaseRequestStepConfigurationViewModel & {
	kind: StepKind.Request;
};

export enum ResultMapperColor {
	Green = 1,
	Blue = 2,
	Orange = 3,
	Red = 4,
	Grey = 5,
	Default = 6,
	Purple = 7,
	Cyan = 8
}

export type ResultMapperColorConditionModel = {
	color: ResultMapperColor;
	conditionExpression: string;
};

export type ResultMapperStyledFieldViewModel = {
	expression: string;
	colors: readonly ResultMapperColorConditionModel[] | null;
};

export type GeneralMappingStepConfigurationViewModel = BaseMappingStepConfigurationViewModel & {
	linkExpression: string;
	title: ResultMapperStyledFieldViewModel;
	date: ResultMapperStyledFieldViewModel;
	subtitle1: ResultMapperStyledFieldViewModel;
	subtitle2: ResultMapperStyledFieldViewModel;
	subtitle3: ResultMapperStyledFieldViewModel;
	description: ResultMapperStyledFieldViewModel;
};

export enum IntegrationFlowKind {
	General = 4
}

export enum IntegrationFlowCapabilityKind {
	Search = 1
}

export type BaseMappingStepConfigurationViewModel = {
	kind: StepKind.Mapping;
	resultCollectionExpression: string;
};

export type MappingStepConfigurationViewModel =
	| GeneralMappingStepConfigurationViewModel;

export type StepConfigurationViewModel =
	| RequestStepConfigurationViewModel
	| MappingStepConfigurationViewModel
	| PerItemRequestStepConfigurationViewModel;

export enum IntegrationFlowStatus {
	Draft = 1,
	Live = 2,
	Blocked = 3
}

export type IntegrationFlowAuthenticationOptionModel = {
	id: number;
	name: string;
	kind: IntegrationFlowAuthenticationKind;
};

export enum IntegrationSettingScopeKind {
	ThisFlow = 1,
	Shared = 2
}

export type ThisFlowIntegrationFlowSettingViewModel = {
	scopeKind: IntegrationSettingScopeKind.ThisFlow;
	key: string;
	value: string;
	displayName: string;
	description: string | null;
};

export type SharedIntegrationFlowSettingViewModel = {
	scopeKind: IntegrationSettingScopeKind.Shared;
	key: string;
	sharedSettingId: number;
};

export type IntegrationFlowSettingViewModel =
	| ThisFlowIntegrationFlowSettingViewModel
	| SharedIntegrationFlowSettingViewModel;

export type IntegrationFlowConfigurationViewModel = {
	kind: IntegrationFlowKind;
	capabilities: readonly IntegrationFlowCapabilityKind[];
	name: string;
	color: SaveAppColorModel;
	image: AppImageIdentifier;
	everyoneHasAccess: boolean;
	whoHasAccess: PermissionSetModel;
	steps: readonly StepConfigurationViewModel[];
	settings: readonly IntegrationFlowSettingViewModel[];
	sharedSettings: readonly IntegrationSharedSettingOptionModel[];
	status: IntegrationFlowStatus;
	hasLiveVersion: boolean;
	imageDisplayInfo: AppImageDisplayInfo;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
	authentications: readonly IntegrationFlowAuthenticationOptionModel[];
	tags: readonly TagViewModel[];
};

export type IntegrationFlowConfigurationPageViewModel = {
	isAllowedToConfigureSearchSettings: boolean;
	model: IntegrationFlowConfigurationViewModel;
};

export function getNewViewModel(
	appId: number,
	kind: IntegrationFlowKind,
	signal?: AbortSignal
): Promise<IntegrationFlowConfigurationPageViewModel> {
	return environmentHttpClient.get<IntegrationFlowConfigurationPageViewModel>(
		`UiApi/IntegrationBuilder/Apps/${appId}/Flows/New`,
		{ kind },
		{ signal }
	);
}

export function deleteVersions(
	integrationId: number,
	versionIds: readonly Guid[],
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.delete<void>(
		`UiApi/IntegrationBuilder/Flows/${integrationId}/Version`,
		versionIds,
		{ signal }
	);
}

export function getEditViewModel(
	integrationId: number,
	versionId: Guid,
	signal?: AbortSignal
): Promise<IntegrationFlowConfigurationPageViewModel> {
	return environmentHttpClient.get<IntegrationFlowConfigurationPageViewModel>(
		`/UiApi/IntegrationBuilder/Flows/${integrationId}/Version/${encodeURIComponent(versionId)}`,
		undefined,
		{ signal }
	);
}

export type BaseRequestStepConfigurationSaveModel = {
	name: string;
	authenticationId: number | null;
	url: string;
	method: RequestMethod;
	queryParameters: readonly KeyValuePairSaveModel[];
	headers: readonly KeyValuePairSaveModel[];
	body: string | null;
};

export type PerItemRequestStepConfigurationSaveModel = BaseRequestStepConfigurationSaveModel & {
	stepCollection: string;
	maxItems: number;
};

export type RequestStepConfigurationSaveModel = BaseRequestStepConfigurationSaveModel;

export type ResultMapperStyledFieldSaveModel = {
	expression: string;
	colors: readonly ResultMapperColorConditionModel[] | null;
};

export type GeneralMappingStepConfigurationSaveModel = {
	linkExpression: string;
	title: ResultMapperStyledFieldSaveModel;
	date: ResultMapperStyledFieldSaveModel | null;
	subtitle1: ResultMapperStyledFieldSaveModel | null;
	subtitle2: ResultMapperStyledFieldSaveModel | null;
	subtitle3: ResultMapperStyledFieldSaveModel | null;
	description: ResultMapperStyledFieldSaveModel | null;
};

export type MappingStepConfigurationWithGeneralSaveModel = BaseMappingStepConfigurationSaveModel & {
	general: GeneralMappingStepConfigurationSaveModel;
};

type BaseMappingStepConfigurationSaveModel = {
	resultCollectionExpression: string;
};

export type MappingStepConfigurationSaveModel =
	| MappingStepConfigurationWithGeneralSaveModel;

export type StepConfigurationSaveModel = {
	kind: StepKind.Request;
	request: RequestStepConfigurationSaveModel;
} | {
	kind: StepKind.Mapping;
	mapping: MappingStepConfigurationSaveModel;
} | {
	kind: StepKind.PerItemRequest;
	perItemRequest: PerItemRequestStepConfigurationSaveModel;
};

export type ThisFlowIntegrationFlowSettingSaveModel = {
	scopeKind: IntegrationSettingScopeKind.ThisFlow;
	key: string;
	value: string;
	displayName: string;
	description: string;
};

export type SharedIntegrationFlowSettingSaveModel = {
	scopeKind: IntegrationSettingScopeKind.Shared;
	key: string;
	sharedSettingId: number;
};

export type IntegrationFlowSettingSaveModel =
	| ThisFlowIntegrationFlowSettingSaveModel
	| SharedIntegrationFlowSettingSaveModel;

export type IntegrationFlowConfigurationSaveModel = {
	kind: IntegrationFlowKind;
	capabilities: readonly IntegrationFlowCapabilityKind[];
	name: string;
	color: SaveAppColorModel;
	image: AppImageIdentifier;
	permissions: OptionalModel<IntegrationFlowPermissionsSaveModel>;
	steps: readonly StepConfigurationSaveModel[];
	settings: readonly IntegrationFlowSettingSaveModel[];
	inputData: IntegrationFlowInputDataModel;
	tags: readonly TagSaveModel[];
};

export type IntegrationFlowPermissionsViewModel = {
	everyoneHasAccess: boolean;
	whoHasAccess: PermissionSetModel;
	usersAndUserGroupsData: UsersAndUserGroupsDataModel;
};

export type IntegrationFlowPermissionsSaveModel = {
	everyoneHasAccess: boolean;
	whoHasAccess: PermissionSetModel;
};

export function getPermissions(
	integrationId: number,
	signal?: AbortSignal
): Promise<IntegrationFlowPermissionsViewModel> {
	return environmentHttpClient.get<IntegrationFlowPermissionsViewModel>(
		`UiApi/IntegrationBuilder/Flows/${integrationId}/Permissions`,
		undefined,
		{ signal }
	);
}

export function setPermissions(
	integrationId: number,
	model: IntegrationFlowPermissionsSaveModel,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		`UiApi/IntegrationBuilder/Flows/${integrationId}/Permissions`,
		model,
		{ signal }
	);
}

export type CreateIntegrationFlowResultModel = {
	integrationId: number;
	versionId: Guid;
};

export function createFlow(
	appId: number,
	model: IntegrationFlowConfigurationSaveModel,
	isLive: boolean,
	signal?: AbortSignal
): Promise<CreateIntegrationFlowResultModel> {
	return environmentHttpClient.post<CreateIntegrationFlowResultModel>(
		`UiApi/IntegrationBuilder/Apps/${appId}/Flows?isLive=${isLive}`,
		model,
		{ signal }
	);
}

export function createDraft(
	integrationId: number,
	versionId: Guid,
	signal?: AbortSignal
): Promise<CreateIntegrationFlowResultModel> {
	return environmentHttpClient.post<CreateIntegrationFlowResultModel>(
		`UiApi/IntegrationBuilder/Flows/${integrationId}/Version?versionId=${encodeURIComponent(versionId)}`,
		undefined,
		{ signal }
	);
}

export function editFlow(
	integrationId: number,
	versionId: Guid,
	model: IntegrationFlowConfigurationSaveModel,
	isLive: boolean,
	signal?: AbortSignal
): Promise<void> {
	return environmentHttpClient.put<void>(
		`UiApi/IntegrationBuilder/Flows/${integrationId}/Version/${encodeURIComponent(versionId)}?isLive=${isLive}`,
		model,
		{ signal }
	);
}

export type IntegrationFlowInputDataModel = {
	search: OptionalModel<string>;
};

export type IntegrationFlowConfigurationValidateModel = {
	kind: IntegrationFlowKind;
	capabilities: readonly IntegrationFlowCapabilityKind[];
	steps: readonly StepConfigurationSaveModel[];
	settings: readonly IntegrationFlowSettingSaveModel[];
	inputData: IntegrationFlowInputDataModel;
};

export type GeneralIntegrationFlowValidationResultSampleFieldModel<T> = {
	value: T | null;
	colorConditionIndex: number | null;
};

export type IntegrationFlowValidationResultGeneralSampleModel = {
	link: string | null;
	title: GeneralIntegrationFlowValidationResultSampleFieldModel<string> | null;
	subTitle1: GeneralIntegrationFlowValidationResultSampleFieldModel<string> | null;
	subTitle2: GeneralIntegrationFlowValidationResultSampleFieldModel<string> | null;
	subTitle3: GeneralIntegrationFlowValidationResultSampleFieldModel<string>;
	description: GeneralIntegrationFlowValidationResultSampleFieldModel<string> | null;
	dateTime: GeneralIntegrationFlowValidationResultSampleFieldModel<Date | string> | null;
};

export type GeneralIntegrationFlowValidationResultModel = IntegrationFlowValidationResultModel
	& {
		generalSample: OptionalModel<IntegrationFlowValidationResultGeneralSampleModel>;
	};

export type IntegrationFlowValidationResultModel = {
	requestResults: readonly IntegrationFlowRequestResultModel[];
	validationErrors: readonly ClientValidationErrorModel[];
};

export type IntegrationFlowRequestResultModel = {
	name: string;
	statusCode: number | null;
	reasonPhrase: string | null;
	content: string | null;
	isBinaryContent: boolean;
};

export type IntegrationFlowConfigurationValidateResponseModel =
	| GeneralIntegrationFlowValidationResultModel;

export function validate(
	appId: number,
	model: IntegrationFlowConfigurationValidateModel,
	signal?: AbortSignal
): Promise<IntegrationFlowConfigurationValidateResponseModel> {
	return environmentHttpClient.post<IntegrationFlowConfigurationValidateResponseModel>(
		`UiApi/IntegrationBuilder/Apps/${appId}/Flows/ValidateSteps`,
		model,
		{ signal }
	);
}
